/**
 * Chip Field Type
 *
 * Provides custom column renderer for chip display cells.
 * This type is read-only only (custom chips are not editable inline).
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Chip } from 'components';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../types';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Chip read-only renderer - renders a styled chip with custom label and background
 * Requires chipConfig with getLabel and getBackground functions
 */
export const chipReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  if (!col.chipConfig) throw new Error(`chipConfig required for chip column: ${col.id}`);

  const chipConfig = col.chipConfig;
  return {
    cell: ({ row }: CellContext<T, unknown>) => (
      <Chip
        sx={{ padding: '4px 16px' }}
        isBordered={false}
        label={chipConfig.getLabel(row.original)}
        background={chipConfig.getBackground(row.original)}
      />
    ),
    tooltipRender: (cell: CellContext<T, unknown>) => {
      return chipConfig.getLabel(cell.row.original);
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const chipReadOnly: ColumnRenderer = chipReadOnlyRenderer;

/** Editable renderer - null because chip columns are not editable */
export const chipEditable: ColumnRenderer | null = null;
